import React from 'react'
import DateTimePicker from '@/components/ui/DateTimePicker'
import { Select } from '@/components/ui/Select'
import { Field } from '@/components/ui/Field'
import type { WorkflowNodeData } from '@/types/workflow'

interface DelayConfigProps {
  data: WorkflowNodeData
  updateField: (field: string, value: any) => void
}

import {
  inputClass,
  inputStyle,
  sectionClass,
  infoBannerClass,
  checkboxInputClass,
  checkboxLabelClass,
} from './_kit'
import { cn } from '@/lib/utils'

const TIME_UNIT_OPTIONS = [
  { value: 'minutes', label: 'Minutes' },
  { value: 'hours', label: 'Hours' },
  { value: 'days', label: 'Days' },
]

const EVENT_TYPE_OPTIONS = [
  { value: 'email_opened', label: 'Email Opened' },
  { value: 'email_clicked', label: 'Link Clicked' },
  { value: 'email_replied', label: 'Email Replied' },
]

const eventLabel = (value?: string) =>
  EVENT_TYPE_OPTIONS.find((o) => o.value === value)?.label || 'Event'

/** Human-readable label that mirrors the configured wait. Kept in sync with the
 *  node title so changing the duration doesn't leave a stale "Wait 3 Days". */
const deriveDelayLabel = (d: WorkflowNodeData): string => {
  const mode = d.delay_mode || 'duration'
  if (mode === 'wait_for_event') {
    return `Wait for ${eventLabel(d.event_type || 'email_opened')}`
  }
  if (mode === 'wait_until_time') {
    return `Wait until ${d.wait_until_time || '09:00'}`
  }
  return `Wait ${d.delay_value ?? 1} ${d.delay_unit || 'hours'}`
}

/** True when the label is empty or still one of the auto-generated forms that
 *  templates / the AI wizard / deriveDelayLabel produce — i.e. not a custom
 *  name the user deliberately typed (e.g. "Cooldown before pitch"). */
const isAutoDelayLabel = (label?: string): boolean => {
  const l = (label || '').trim()
  if (!l) return true
  return (
    /^wait\s+\d+\s+(minutes?|hours?|days?)$/i.test(l) ||
    /^wait until\b/i.test(l) ||
    /^wait for\b/i.test(l)
  )
}

const DelayConfig: React.FC<DelayConfigProps> = ({ data, updateField }) => {
  const mode = data.delay_mode || 'duration'

  // Update a delay parameter and, when the label is still auto-generated,
  // re-derive the label so the canvas node title tracks the new value.
  const updateDelayField = (field: string, value: any) => {
    updateField(field, value)
    if (isAutoDelayLabel(data.label)) {
      updateField('label', deriveDelayLabel({ ...data, [field]: value }))
    }
  }

  // Switch wait mode and, when entering "For Event", persist the event defaults.
  // The Select below only DISPLAYS 'email_opened' via `|| 'email_opened'`; it
  // never writes it unless the user changes the dropdown, so leaving the default
  // saved event_type as null and the executor never matched the open/click/reply
  // (it timed out instead — campaign 191). Seed the defaults explicitly here.
  const selectMode = (mode: string) => {
    updateDelayField('delay_mode', mode)
    if (mode === 'wait_for_event') {
      if (!data.event_type) updateField('event_type', 'email_opened')
      if (!data.event_timeout_value) updateField('event_timeout_value', 3)
      if (!data.event_timeout_unit) updateField('event_timeout_unit', 'days')
    }
  }

  return (
    <div className={sectionClass}>
      <Field label="Label">
        {(f) => (
          <input
            {...f}
            type="text"
            value={data.label || ''}
            onChange={(e) => updateField('label', e.target.value)}
            placeholder="e.g., Wait 2 days"
            className={inputClass} style={inputStyle}
          />
        )}
      </Field>

      {/* Mode Toggle — 3 modes */}
      <Field label="Wait Mode" as="group">
        <div className="flex rounded-lg border border-border/60 overflow-hidden">
          {([
            { key: 'duration', label: 'Duration' },
            { key: 'wait_until_time', label: 'Until Time' },
            { key: 'wait_for_event', label: 'For Event' },
          ] as const).map(m => (
            <button
              key={m.key}
              type="button"
              onClick={() => selectMode(m.key)}
              className={`flex-1 px-3 py-2 text-xs font-medium transition-all duration-150 ${
                mode === m.key
                  ? 'bg-amber-500 text-white'
                  : 'bg-card text-muted-foreground hover:bg-muted'
              }`}
            >
              {m.label}
            </button>
          ))}
        </div>
      </Field>

      {/* Duration mode */}
      {mode === 'duration' && (
        <div className="grid grid-cols-2 gap-3">
          <Field label="Duration">
            {(f) => (
              <input
                {...f}
                type="number"
                min={1}
                value={data.delay_value || ''}
                onChange={(e) => updateDelayField('delay_value', parseInt(e.target.value) || 0)}
                placeholder="1"
                className={inputClass} style={inputStyle}
              />
            )}
          </Field>
          <Field label="Unit">
            {(f) => (
              <Select
                {...f}
                value={data.delay_unit || 'hours'}
                onChange={(value) => updateDelayField('delay_unit', value)}
                options={TIME_UNIT_OPTIONS}
                className="w-full"
              />
            )}
          </Field>
        </div>
      )}

      {/* Wait Until Time mode */}
      {mode === 'wait_until_time' && (
        <Field
          label="Wait Until"
          as="group"
          hint="Waits until this time in the contact's timezone"
        >
          <DateTimePicker
            mode="time"
            value={data.wait_until_time || '09:00'}
            onChange={(v) => updateDelayField('wait_until_time', v)}
            ariaLabel="Wait until time"
          />
        </Field>
      )}

      {/* Wait for Event mode */}
      {mode === 'wait_for_event' && (
        <div className="space-y-3">
          <Field label="Wait For">
            {(f) => (
              <Select
                {...f}
                value={data.event_type || 'email_opened'}
                onChange={(value) => updateDelayField('event_type', value)}
                options={EVENT_TYPE_OPTIONS}
                className="w-full"
              />
            )}
          </Field>

          <Field label="Timeout After" as="group">
            <div className="grid grid-cols-2 gap-3">
              <input
                type="number"
                min={1}
                value={data.event_timeout_value || 3}
                onChange={(e) => updateField('event_timeout_value', parseInt(e.target.value) || 1)}
                className={inputClass} style={inputStyle}
              />
              <Select
                value={data.event_timeout_unit || 'days'}
                onChange={(value) => updateField('event_timeout_unit', value)}
                options={TIME_UNIT_OPTIONS}
                ariaLabel="Timeout unit"
                className="w-full"
              />
            </div>
          </Field>

          <div className={cn(infoBannerClass, 'flex-col space-y-1')}>
            <p className="text-xs">
              <strong>Event path (green):</strong> Contacts who trigger the event within the timeout.
            </p>
            <p className="text-xs">
              <strong>Timeout path (orange):</strong> Contacts who don't trigger the event before timeout.
            </p>
          </div>
        </div>
      )}

      {/* Business Days — only for duration/wait_until_time */}
      {mode !== 'wait_for_event' && (
        <label className={checkboxLabelClass}>
          <input
            type="checkbox"
            checked={data.business_days_only || false}
            onChange={(e) => updateField('business_days_only', e.target.checked)}
            className={checkboxInputClass}
          />
          <span className="text-sm">Business days only</span>
        </label>
      )}
    </div>
  )
}

export default DelayConfig
